/* eslint-disable import/no-anonymous-default-export */
/**
 * ETC 미션 신청 캠페인 설정
 */
import RefundBadge from "../components/RefundBadge";
import {
  CHANNEL_OPTIONS,
  PRODUCT_OPTIONS,
  ALL_ETC_TYPES,
  channelProductToTypes,
  getCampaignTypeLabel,
} from "../../../constants/etcMissionTypes";

// action: completed 시 타입별 고유 ID 필드 매핑
const CAMPAIGN_TYPE_ID_FIELD = {
  follow_on_daangn: "adFollowOnDaangnId",
  yanolja_wish: "adYanoljaWishId",
  catchtable_save: "adCatchtableSaveId",
  kakao_channel_add: "adKakaoChannelAddId",
  blog_post_like: "adBlogPostLikeId",
  blog_post_keep: "adBlogPostKeepId",
};

// by-type API는 paidAt/isRefunded, 레거시 follow-on-daangn은 isPaid를 사용한다
// isRefunded === 1 우선 체크: isPaid가 1이어도 환불됐으면 2(환불) 반환
function getIsPaid(item) {
  if (item.isRefunded === 1) return 2;
  if (item.isPaid !== undefined) return item.isPaid;
  if (item.paidAt) return 1;
  return 0;
}

// status 기준 isSubmitted 판단 (레거시는 isSubmitted 직접 사용)
function getIsSubmitted(item) {
  if (item.isSubmitted !== undefined) return item.isSubmitted;
  return item.status !== "waiting" ? 1 : 0;
}

// status 기준 isCompleted 판단 (레거시는 isCompleted 직접 사용)
function getIsCompleted(item) {
  if (item.isCompleted !== undefined) return item.isCompleted;
  return item.status === "completed" ? 1 : 0;
}

export default {
  id: "adEtcMission",
  title: "ETC 미션 신청",

  api: {
    list: {
      method: "GET",
      url: "/advertisement/admin/campaigns/by-type",
      params: {
        types: ALL_ETC_TYPES.join(","),
      },
      paramsTransformer: (params) => {
        const { channelNames = [], productNames = [], ...rest } = params;
        const selectedTypes = channelProductToTypes(channelNames, productNames);
        return {
          ...rest,
          types: selectedTypes.length > 0 ? selectedTypes.join(",") : ALL_ETC_TYPES.join(","),
        };
      },
    },
    count: {
      method: "GET",
      url: "/advertisement/admin/campaigns/by-type/count",
      params: {
        types: ALL_ETC_TYPES.join(","),
      },
      paramsTransformer: (params) => {
        const { channelNames = [], productNames = [], ...rest } = params;
        const selectedTypes = channelProductToTypes(channelNames, productNames);
        return {
          ...rest,
          types: selectedTypes.length > 0 ? selectedTypes.join(",") : ALL_ETC_TYPES.join(","),
        };
      },
    },
    submitted: {
      method: "POST",
      url: "/advertisement/admin/campaigns/by-type/status",
    },
    paid: {
      method: "POST",
      url: "/advertisement/admin/campaigns/by-type/status",
    },
    complete: {
      method: "POST",
      url: "/advertisement/admin/campaigns/by-type/status",
    },
    refund: {
      method: "POST",
      url: "/advertisement/admin/campaigns/by-type/status",
      version: 2,
    },
    delete: {
      method: "DELETE",
      url: "/advertisement/admin/campaigns/by-type",
    },
  },

  filters: {
    default: {
      status: "전체",
      submitDate: "",
      keyword: "",
      page: 1,
      limit: 50,
      submitted: "전체",
      paid: "전체",
      channelNames: [],
      productNames: [],
    },
    schema: [
      {
        name: "keyword",
        label: "검색",
        type: "text",
        placeholder: "신청자, 플레이스명",
      },
      {
        name: "channelNames",
        label: "채널명",
        type: "multi",
        multi: true,
        options: CHANNEL_OPTIONS,
      },
      {
        name: "productNames",
        label: "상품명",
        type: "multi",
        multi: true,
        options: PRODUCT_OPTIONS,
      },
      {
        name: "submitDate",
        label: "접수일",
        type: "date",
      },
      {
        name: "submitted",
        label: "접수여부",
        type: "select",
        options: [
          { value: "전체", label: "전체" },
          { value: "complete", label: "접수완료" },
          { value: "notComplete", label: "미접수" },
        ],
      },
      {
        name: "paid",
        label: "결제상태",
        type: "select",
        options: [
          { value: "전체", label: "전체" },
          { value: "complete", label: "결제완료" },
          { value: "notComplete", label: "미결제" },
          { value: "refund", label: "환불" },
        ],
      },
      {
        name: "status",
        label: "상태",
        type: "select",
        options: [
          { value: "전체", label: "전체" },
          { value: "waiting", label: "대기중" },
          { value: "processing", label: "진행중" },
          { value: "completed", label: "종료" },
        ],
      },
    ],
  },

  summary: {
    fields: [
      { key: "totalCount", label: "총 신청" },
      { key: "waitingCount", label: "대기중" },
      { key: "processingCount", label: "진행중" },
      { key: "completedCount", label: "종료" },
    ],
  },

  table: {
    keyField: "adCampaignMetaId",
    columns: [
      {
        key: "adCampaignMetaId",
        label: "아이디",
        center: true,
      },
      {
        key: "createdAt",
        label: "캐랭접수시간",
        center: true,
        render: (row) => {
          if (!row.createdAt) return "";
          const [date, time] = row.createdAt.split(" ");
          return (
            <>
              <div>{date}</div>
              <div>{time?.slice(0, 8)}</div>
            </>
          );
        },
      },
      {
        key: "userName",
        label: "신청자(ID)\n상태",
        center: true,
        render: (row) => (
          <>
            <div>
              {row.userName}
              {row.userId ? ` (${row.userId})` : ""}
            </div>
            <div>
              <span className={`badge badge-${row.status}`}>
                {getStatusLabel(row.status)}
              </span>
            </div>
          </>
        ),
      },
      {
        key: "submitDate",
        label: "접수일",
        center: true,
        render: (row) => (row.createdAt ? row.createdAt.split(" ")[0] : ""),
      },
      {
        key: "channelProduct",
        label: "채널명 / 상품명\n일요청수 / 진행일수",
        center: true,
        render: (row) => {
          const channelLabel = row.channelName || getCampaignTypeLabel(row.campaignType).channelLabel;
          const productLabel = row.productName || getCampaignTypeLabel(row.campaignType).productLabel;
          return (
            <>
              <div>{channelLabel} / {productLabel}</div>
              <div>
                {row.dailyRequestCount != null ? `${row.dailyRequestCount}건` : (row.requestCount != null ? `${row.requestCount}건` : "")}
                {" / "}
                {row.progressDays != null ? `${row.progressDays}일` : "-"}
              </div>
            </>
          );
        },
      },
      {
        key: "placeInfo",
        label: "상호명 (서브)\n연락처\nURL",
        center: true,
        render: (row) => (
          <>
            <div>
              {row.placeName || row.placeUrl || ""}
              {row.subPlaceName
                ? ` (${row.subPlaceName})`
                : " (-)"}
            </div>
            <div>{row.contactNumber || row.phoneNum || ""}</div>
            <div>
              {(row.url || row.placeUrl) && (
                <a
                  href={row.url || row.placeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {row.url || row.placeUrl}
                </a>
              )}
            </div>
          </>
        ),
      },
      {
        key: "totalAmount",
        label: "총금액",
        center: true,
        render: (row) =>
          row.totalAmount ? `${row.totalAmount.toLocaleString()}원` : "",
      },
      {
        key: "paymentType",
        label: "결제방법\n결제상태",
        center: true,
        render: (row) => (
          <>
            <div>
              {row.paymentType === "card"
                ? "카드"
                : row.paymentType === "v_bank"
                  ? "가상계좌"
                  : row.paymentType === "point"
                    ? "포인트 결제"
                    : "계좌이체"}
            </div>
            <div>
              {getIsPaid(row) === 2 ? (
                <RefundBadge
                  refundedAmount={row.refundedAmount ?? 0}
                  remainingAmount={row.remainingAmount ?? 0}
                  refundDate={row.refundDate}
                />
              ) : (
                <span
                  className={`badge badge-${getIsPaid(row) === 1 ? "결제완료" : "미결제"}`}
                >
                  {getIsPaid(row) === 1 ? "결제완료" : "미결제"}
                </span>
              )}
            </div>
          </>
        ),
      },
      {
        key: "report",
        label: "리포트",
        center: true,
        render: (row, handlers) => {
          console.log("Report button render. Row:", row);
          console.log("Report button render. Handlers:", handlers);
          return (
            <button
              type="button"
              className="status-btn submitted"
              onClick={(e) => {
                e.stopPropagation();
                if (handlers && handlers.openReportModal) {
                  handlers.openReportModal(row.adCampaignMetaId, row.placeName);
                }
              }}
            >
              리포트
            </button>
          );
        },
      },
      {
        key: "notification",
        label: "알림톡",
        center: true,
        render: (row) => {
          const productName = `${row.channelName || ""} ${row.productName || ""}`.trim();

          return (
            <button
              type="button"
              className="notification-btn"
              data-campaign-meta-id={row.adCampaignMetaId}
              data-action="notification"
              data-user-id={row.userId}
              data-product-name={productName}
              data-submitted-at={row.createdAt || ""}
              data-purchase-amount={row.totalAmount ?? ""}
              onClick={(e) => e.stopPropagation()}
            >
              알림톡
            </button>
          );
        },
      },
      {
        key: "memo",
        label: "메모",
        center: true,
        render: (row) => (
          <button
            type="button"
            className={row.memo ? "memo-btn has-memo" : "memo-btn"}
            data-action="memo"
            data-campaign-meta-id={row.adCampaignMetaId}
            data-memo={row.memo || ""}
            onClick={(e) => e.stopPropagation()}
          >
            {row.memo ? (
              <span className="memo-preview">{row.memo}</span>
            ) : (
              "메모"
            )}
          </button>
        ),
      },
    ],
  },

  actions: [
    {
      id: "submitted",
      label: "접수/취소",
      actionType: "submitted",
      enabledWhen: (selectedIds) => selectedIds.length > 0,
      payloadBuilder: (selectedIds, items) => {
        const hasUnsubmitted = items.some(
          (item) => selectedIds.includes(item.adCampaignMetaId) && item.isSubmitted !== 1,
        );
        return {
          ids: selectedIds,
          toggleValue: hasUnsubmitted ? 1 : 0,
        };
      },
      groupPayloadBuilder: (type, typeIds, typeItems) => {
        const hasUnsubmitted = typeItems.some((item) => getIsSubmitted(item) !== 1);
        return {
          type,
          action: "submitted",
          ids: typeIds,
          toggleValue: hasUnsubmitted ? 1 : 0,
        };
      },
    },
    {
      id: "complete",
      label: "완료(종료)",
      actionType: "complete",
      enabledWhen: (selectedIds) => selectedIds.length > 0,
      confirm: (selectedIds, items) => {
        const hasUncompleted = items.some(
          (item) => selectedIds.includes(item.adCampaignMetaId) && getIsCompleted(item) !== 1,
        );
        return hasUncompleted
          ? "선택한 신청을 완료 처리하시겠습니까?"
          : "선택한 신청을 취소 처리하시겠습니까?";
      },
      payloadBuilder: (selectedIds, items) => {
        const hasUncompleted = items.some(
          (item) => selectedIds.includes(item.adCampaignMetaId) && getIsCompleted(item) !== 1,
        );
        return {
          ids: selectedIds,
          toggleValue: hasUncompleted ? 1 : 0,
        };
      },
      groupPayloadBuilder: (type, typeIds, typeItems) => {
        const idField = CAMPAIGN_TYPE_ID_FIELD[type] || "adCampaignMetaId";
        const specificIds = typeItems.map(item => item[idField]).filter(Boolean);
        const hasUncompleted = typeItems.some((item) => getIsCompleted(item) !== 1);
        return {
          type,
          action: "completed",
          ids: specificIds,
          toggleValue: hasUncompleted ? 1 : 0,
        };
      },
    },
    {
      id: "paid",
      label: "결제완료",
      actionType: "paid",
      enabledWhen: (selectedIds, items) => {
        if (selectedIds.length === 0) return false;
        return !items.some(
          (item) => selectedIds.includes(item.adCampaignMetaId) && getIsPaid(item) === 1,
        );
      },
      payloadBuilder: (selectedIds) => ({
        ids: selectedIds,
        toggleValue: 1,
      }),
      groupPayloadBuilder: (type, typeIds, typeItems) => ({
        type,
        action: "paid",
        ids: typeItems.map(item => item.adPaymentTransactionId).filter(Boolean),
        toggleValue: 1,
      }),
    },
    {
      id: "refund",
      label: "환불",
      actionType: "refund",
      requires: "ad.campaign.refund", // 권한 없으면 버튼 숨김 (R-FE-04)
      enabledWhen: (selectedIds, items) => {
        if (selectedIds.length === 0) return false;
        return items.some((item) => {
          if (!selectedIds.includes(item.adCampaignMetaId)) return false;
          const paid = getIsPaid(item);
          return (paid === 1 || paid === 2) && (item.remainingAmount ?? item.totalAmount ?? 0) > 0;
        });
      },
    },
    {
      id: "delete",
      label: "삭제",
      actionType: "delete",
      enabledWhen: (selectedIds) => selectedIds.length > 0,
      confirm: "선택한 신청을 삭제하시겠습니까?",
      payloadBuilder: (selectedIds) => ({
        ids: selectedIds,
      }),
      groupPayloadBuilder: (type, typeIds, typeItems) => {
        const idField = CAMPAIGN_TYPE_ID_FIELD[type] || "adCampaignMetaId";
        return {
          type,
          ids: typeItems.map(item => item[idField]).filter(Boolean),
        };
      },
    },
  ],

  download: {
    type: "xlsx",
    fileName: "ETC미션_신청목록.xlsx",
    sheetName: "ETC미션신청목록",
    headers: [
      "신청자",
      "상품명",
      "접수일",
      "시작일",
      "종료일",
      "작업일",
      "총요청수",
      "하루참여",
      "상호명",
      "상호명",
      "비즈url",
      "총금액",
      "결제방법",
      "연락처",
    ],
    transformRow: (row) => [
      row.userName || "",
      row.title || row.productName || "",
      row.createdAt ? row.createdAt.split(" ")[0] : "",
      "",
      "",
      row.progressDays ?? "",
      row.totalCount || 0,
      row.dailyRequestCount ?? row.requestCount ?? "",
      row.placeName || "",
      row.subPlaceName || row.placeName || "",
      row.url || row.placeUrl || "",
      row.totalAmount || 0,
      row.paymentType === "card"
        ? "카드"
        : row.paymentType === "v_bank"
          ? "가상계좌"
          : row.paymentType === "point"
            ? "포인트 결제"
            : "계좌이체",
      row.contactNumber || row.phoneNum || "",
    ],
  },
};

function getStatusLabel(status) {
  switch (status) {
    case "waiting":
      return "대기중";
    case "processing":
      return "진행중";
    case "completed":
      return "종료";
    default:
      return status;
  }
}
